import type JsFrontendState from "@/shared/model/user/js-cache/JsFrontendState";
import type { LegacyJsFrontendState } from "@/shared/model/user/js-cache/JsFrontendState";
import { compressToUTF16, decompressFromUTF16 } from "lz-string";
import sendBroadcastAction from "@/shared/services/broadcast/SendBroadcastAction.ts";

// Key for localStorage data representing global front end state:
export const FRONTEND_STATE_KEY = "frontend_state";

// enum key for localStorage data:
export enum FrontendStateKey {
  DOMAIN_ID = "idDomain",
  SITE_ID = "idSite",
  IDS_SITES = "idsSites",
  DATES = "dates",
}

export function buildFrontendStateFromLocalStorage(): JsFrontendState {
  const defaultState = {
    domainId: undefined,
    siteId: undefined,
    siteIds: {},
    dates: {},
  };
  const storedItem = localStorage.getItem(FRONTEND_STATE_KEY);
  if (storedItem == null) return defaultState;

  // Decompress the UTF-16 string
  const decompressed = decompressFromUTF16(storedItem);
  if (decompressed == "") return defaultState; // corrupted or empty data
  try {
    return buildFrontendStateObject(decompressed);
  } catch (e) {
    // SyntaxError: Unexpected token '@', "@@@" is not valid JSON
    console.log(e);
  }
  return defaultState;
}

export function writeFrontendStateToLocalStorage(
  LocalStorageKey: FrontendStateKey,
  value: unknown,
): void {
  const frontendState = buildFrontendStateFromLocalStorage();
  switch (LocalStorageKey) {
    case FrontendStateKey.DOMAIN_ID:
      frontendState.domainId = value as string;
      break;
    case FrontendStateKey.SITE_ID:
      frontendState.siteId = value as string;
      break;
    case FrontendStateKey.IDS_SITES:
      frontendState.siteIds = value as Record<string, string[]>;
      break;
    case FrontendStateKey.DATES:
      frontendState.dates = value as Record<string, string | undefined>;
      break;
  }

  const compressed = compressToUTF16(
    JSON.stringify(buildLegacyFrontendStateObject(frontendState)),
  );
  localStorage.setItem(FRONTEND_STATE_KEY, compressed);
  sendBroadcastAction({ action: "frontendStateUpdated" });
}

export function writeDomainIdToLocalStorage(domainId: string): void {
  writeFrontendStateToLocalStorage(
    FrontendStateKey.DOMAIN_ID,
    domainId.toString(),
  );
}

export function writeSiteIdToLocalStorage(siteId: string): void {
  writeFrontendStateToLocalStorage(FrontendStateKey.SITE_ID, siteId.toString());
}

export function writeIdsSitesToLocalStorage(
  idsSites: Record<string, string[]>,
): void {
  writeFrontendStateToLocalStorage(
    FrontendStateKey.IDS_SITES,
    JSON.stringify(idsSites),
  );
}

export function writeDateToLocalStorage(date: string): void {
  writeFrontendStateToLocalStorage(FrontendStateKey.DATES, date);
}

export function buildFrontendStateObject(
  frontendState: string,
): JsFrontendState {
  const jsonObject = JSON.parse(frontendState) as LegacyJsFrontendState;
  return {
    domainId: jsonObject.idDomain?.toString(),
    siteId: jsonObject.idSite?.toString(),
    siteIds: Object.fromEntries(
      Object.entries(jsonObject.idsSites).map(([key, value]) => [
        key,
        value.map((v) => v.toString()),
      ]),
    ),
    dates: jsonObject.dates,
  };
}

export function buildLegacyFrontendStateObject(
  frontendState: JsFrontendState,
): LegacyJsFrontendState {
  return {
    idDomain: parseInt(frontendState.domainId ?? "0"),
    idSite: parseInt(frontendState.siteId ?? "0"),
    idsSites: Object.fromEntries(
      Object.entries(frontendState.siteIds).map(([key, value]) => [
        key,
        value.map((v) => parseInt(v)),
      ]),
    ),
    dates: frontendState.dates,
  };
}
